import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import GlassCard from "../components/GlassCard";
import PrimaryButton, { GlassButton } from "../components/PrimaryButton";
import BottomNav from "../components/BottomNav";
import PageShell from "../components/PageShell";
import { Copy, Check } from "lucide-react";

const Settings = () => {
  const { user, refresh, logout } = useAuth();
  const nav = useNavigate();
  const [openaiKey, setOpenaiKey] = useState(user?.openai_api_key || "");
  const [resendKey, setResendKey] = useState(user?.resend_api_key || "");
  const [savingKeys, setSavingKeys] = useState(false);
  const [savedKeys, setSavedKeys] = useState(false);
  const [clients, setClients] = useState([]);
  const [joinCodeInput, setJoinCodeInput] = useState("");
  const [linking, setLinking] = useState(false);
  const [linkError, setLinkError] = useState("");
  const [linkedPractitioner, setLinkedPractitioner] = useState("");
  const [copied, setCopied] = useState(false);

  const isPractitioner = user?.role === "therapist" || user?.role === "coach";
  const isClient = user?.role === "client";

  useEffect(() => {
    if (isPractitioner) {
      api.get("/my-clients").then((r) => setClients(r.data || [])).catch(() => {});
    }
    if (isClient && user?.linked_practitioner_id) {
      api.get("/auth/me").then((r) => setLinkedPractitioner(r.data?.linked_practitioner_id || ""));
    }
  }, [isPractitioner, isClient, user]);

  if (!user) return null;

  const saveKeys = async () => {
    setSavingKeys(true);
    try {
      await api.put("/settings", { openai_api_key: openaiKey, resend_api_key: resendKey });
      await refresh();
      setSavedKeys(true);
      setTimeout(() => setSavedKeys(false), 1800);
    } finally { setSavingKeys(false); }
  };

  const manageSubscription = async () => {
    try {
      const r = await api.post("/billing/create-checkout", { origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (e) {
      alert(e?.response?.data?.detail || "Could not open subscription portal");
    }
  };

  const onLogout = async () => { await logout(); nav("/", { replace: true }); };

  const copyCode = async () => {
    if (!user.invite_code) return;
    try {
      await navigator.clipboard.writeText(user.invite_code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) { /* ignore */ }
  };

  const linkPractitioner = async () => {
    setLinkError("");
    if (!joinCodeInput.trim()) return;
    setLinking(true);
    try {
      const r = await api.post("/auth/link-practitioner", { invite_code: joinCodeInput.trim() });
      await refresh();
      setJoinCodeInput("");
      setLinkedPractitioner(r.data?.practitioner_name || "Connected");
    } catch (e) {
      setLinkError(e?.response?.data?.detail || "Invalid join code");
    } finally { setLinking(false); }
  };

  return (
    <PageShell>
      <div className="pt-16 pb-32 max-w-2xl mx-auto flex flex-col gap-6 well-page-enter">
        <h1 className="text-3xl font-bold text-[#2A3B32] text-center">Settings</h1>

        <GlassCard data-testid="account-card">
          <h3 className="text-xs font-semibold text-[#8BA38E] uppercase tracking-wider">Account</h3>
          <div className="mt-3 space-y-1">
            <p className="text-base font-semibold text-[#2A3B32]">{user.name}</p>
            <p className="text-sm text-[#4A5D52]">{user.email}</p>
            <p className="text-xs text-[#8A9C91] capitalize">{user.role}</p>
          </div>
        </GlassCard>

        <GlassCard data-testid="subscription-card">
          <h3 className="text-xs font-semibold text-[#8BA38E] uppercase tracking-wider">Subscription</h3>
          <p className="mt-2 text-sm text-[#4A5D52]">
            Status: <span className="font-semibold text-[#2A3B32] capitalize">{user.subscription_status}</span>
          </p>
          {user.subscription_ends_at && (
            <p className="mt-1 text-xs text-[#8B6E1F]">
              Access ends {new Date(user.subscription_ends_at).toLocaleDateString()}
            </p>
          )}
          {user.subscription_status === "past_due" && (
            <p className="mt-1 text-xs text-[#B85C5C]">
              Last payment failed — update your card via Manage Subscription to keep access.
            </p>
          )}
          <p className="mt-1 text-xs text-[#8A9C91]">$4.99/month · Early access</p>
          <div className="mt-4 flex flex-col gap-2">
            <GlassButton onClick={manageSubscription} data-testid="manage-subscription-btn">
              {user.subscription_status === "past_due" ? "Update Payment Method" : "Manage Subscription"}
            </GlassButton>
            {(user.subscription_status === "active" || user.subscription_status === "past_due") &&
             user.stripe_session_id && !String(user.stripe_session_id).startsWith("promo:") && (
              <button
                onClick={async () => {
                  if (!window.confirm("Cancel subscription? You'll keep access until the end of the current period.")) return;
                  try {
                    const r = await api.post("/billing/cancel");
                    await refresh();
                    if (r.data?.canceled_at) {
                      alert("Subscription will end on " + new Date(r.data.canceled_at * 1000).toLocaleDateString());
                    } else {
                      alert("Subscription canceled.");
                    }
                  } catch (e) {
                    alert("Could not cancel: " + (e?.response?.data?.detail || e.message));
                  }
                }}
                data-testid="cancel-subscription-btn"
                className="text-xs text-[#B85C5C] underline underline-offset-4 hover:text-[#8B4747] transition-colors"
              >
                Cancel Subscription
              </button>
            )}
          </div>
        </GlassCard>

        {isPractitioner && (
          <>
            <GlassCard data-testid="join-code-card">
              <h3 className="text-xs font-semibold text-[#8BA38E] uppercase tracking-wider">Your join code</h3>
              <p className="mt-2 text-xs text-[#8A9C91]">Share this with clients so they can connect to you.</p>
              <div className="mt-4 flex items-center justify-between gap-3">
                <p className="text-2xl font-bold text-[#2A3B32] tracking-widest" data-testid="join-code-value">
                  {user.invite_code || "—"}
                </p>
                <button
                  onClick={copyCode}
                  data-testid="copy-code-btn"
                  className="inline-flex items-center gap-2 px-3 py-2 bg-white/60 border border-white/40 rounded-full text-xs font-semibold text-[#2A3B32] hover:bg-white/80 transition-colors"
                >
                  {copied ? <><Check size={14} className="text-[#6A7F6C]" /> Copied</> : <><Copy size={14} /> Copy</>}
                </button>
              </div>
            </GlassCard>

            <GlassCard data-testid="connected-clients-card">
              <h3 className="text-xs font-semibold text-[#8BA38E] uppercase tracking-wider">Connected clients</h3>
              {clients.length === 0 ? (
                <p className="mt-3 text-sm text-[#4A5D52]">No clients connected yet. Share your join code above.</p>
              ) : (
                <ul className="mt-3 divide-y divide-white/40">
                  {clients.map((c) => (
                    <li key={c.user_id} className="py-3 flex items-center justify-between" data-testid={`connected-client-${c.user_id}`}>
                      <div>
                        <p className="text-sm font-semibold text-[#2A3B32]">{c.name}</p>
                        <p className="text-xs text-[#8A9C91]">{c.email}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </GlassCard>
          </>
        )}

        {isClient && (
          <GlassCard data-testid="connect-practitioner-card">
            <h3 className="text-xs font-semibold text-[#8BA38E] uppercase tracking-wider">Connect to Your Practitioner</h3>
            {user.linked_practitioner_id ? (
              <p className="mt-3 text-sm text-[#4A5D52]" data-testid="connected-practitioner-info">
                Connected to <span className="font-semibold text-[#2A3B32]">{user.linked_practitioner_name || "your practitioner"}</span>
                {user.linked_practitioner_role && (
                  <span className="text-[#8A9C91]"> · {user.linked_practitioner_role}</span>
                )}
              </p>
            ) : (
              <>
                <p className="mt-2 text-xs text-[#8A9C91]">Enter the join code your practitioner gave you (e.g. WELL-4821).</p>
                <input
                  value={joinCodeInput}
                  onChange={(e) => setJoinCodeInput(e.target.value.toUpperCase())}
                  placeholder="WELL-XXXX"
                  maxLength={20}
                  data-testid="join-code-input"
                  className="mt-3 w-full h-[48px] bg-white/60 border border-white/40 text-[#2A3B32] rounded-[16px] px-4 focus:outline-none focus:ring-2 focus:ring-[#8BA38E] tracking-widest font-semibold"
                />
                {linkError && <p className="mt-2 text-sm text-[#B85C5C]" data-testid="link-error">{linkError}</p>}
                <div className="mt-4">
                  <PrimaryButton onClick={linkPractitioner} disabled={linking || !joinCodeInput.trim()} data-testid="link-practitioner-btn">
                    {linking ? "Connecting…" : "Connect"}
                  </PrimaryButton>
                </div>
              </>
            )}
          </GlassCard>
        )}

        {isPractitioner && (
          <GlassCard data-testid="api-keys-card">
            <h3 className="text-xs font-semibold text-[#8BA38E] uppercase tracking-wider">API Keys (optional)</h3>
            <p className="mt-2 text-xs text-[#8A9C91]">Leave blank to use Well&apos;s built-in keys.</p>
            <label className="mt-4 block text-sm text-[#4A5D52]">OpenAI API key</label>
            <input
              type="password"
              value={openaiKey}
              onChange={(e) => setOpenaiKey(e.target.value)}
              placeholder="sk-…"
              data-testid="openai-key-input"
              className="mt-2 w-full h-[48px] bg-white/60 border border-white/40 text-[#2A3B32] rounded-[16px] px-4 focus:outline-none focus:ring-2 focus:ring-[#8BA38E]"
            />
            <label className="mt-4 block text-sm text-[#4A5D52]">Resend API key</label>
            <input
              type="password"
              value={resendKey}
              onChange={(e) => setResendKey(e.target.value)}
              placeholder="re_…"
              data-testid="resend-key-input"
              className="mt-2 w-full h-[48px] bg-white/60 border border-white/40 text-[#2A3B32] rounded-[16px] px-4 focus:outline-none focus:ring-2 focus:ring-[#8BA38E]"
            />
            <div className="mt-6">
              <PrimaryButton onClick={saveKeys} disabled={savingKeys} data-testid="save-keys-btn">
                {savingKeys ? "Saving…" : savedKeys ? "Saved" : "Save Keys"}
              </PrimaryButton>
            </div>
          </GlassCard>
        )}

        <PrimaryButton onClick={onLogout} className="!bg-white/50 !text-[#2A3B32] hover:!bg-white/60 border border-white/40" data-testid="signout-btn">
          Sign Out
        </PrimaryButton>
      </div>
      <BottomNav />
    </PageShell>
  );
};

export default Settings;
